import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

interface Props {
  link?: ILink;
  description?: string;
  imageList: IImage[];
  title: string;
  meta: IMeta;
}

const AlternativeHero = ({
  description,
  imageList,
  link,
  meta,
  title,
}: Readonly<Props>) => {
  const isTablet = useMediaQuery(1440);
  const isMobile = useMediaQuery(768);
  const isThreeImages = imageList.length === 3;
  const isTwoImagesAndOnMobile = !isThreeImages && isMobile;
  const isTabletDevice = isTablet && !isMobile;

  const getImagesToMap = () =>
    isTabletDevice && !isThreeImages
      ? [
          imageList[0],
          imageList[1],
          {
            // As we're duplicating the image, change the mapped key
            ...imageList[0],
            meta: { contentLink: imageList[0]?.altText },
          } as IImage,
        ]
      : imageList;

  return (
    <section
      className="bg-grey-600 text-white"
      data-epi-block-id={meta?.contentLink}
      data-testid="Alternative-Hero"
    >
      <div
        className={classNames(
          'overflow-hidden py-10 md:container xl:flex xl:flex-row-reverse xl:py-[90px]',
          { container: !isThreeImages }
        )}
      >
        <div
          className={classNames(
            'relative mb-10 flex items-center justify-center gap-5 overflow-hidden xl:mb-0 xl:flex-[2]',
            isThreeImages
              ? 'w-[120vw] translate-x-[-10vw] md:w-auto md:translate-x-0 xl:w-full xl:gap-12'
              : 'xl:gap-10'
          )}
        >
          {getImagesToMap().map((image, index) => (
            <div
              key={image?.meta?.contentLink}
              className={classNames(
                'relative overflow-hidden',
                isThreeImages
                  ? 'h-[200px] w-full md:h-[300px] xl:h-[390px]'
                  : 'h-[150px] md:h-[200px] lg:h-[300px] xl:h-[433px]',
                {
                  'flex-1 md:flex-auto': isTwoImagesAndOnMobile && index === 0,
                  'flex-[2] md:flex-auto':
                    isTwoImagesAndOnMobile && index === 1,
                }
              )}
            >
              <Image
                image={image}
                epiEdit={meta?.editHints?.imageList as string | undefined}
                imageClasses={classNames(
                  'w-full h-full object-cover object-center',
                  isThreeImages ? 'aspect-2/3' : index % 2 !== 0 && 'aspect-3/2'
                )}
                dataTestId={`Alternative-Hero-Image-${index}`}
              />
            </div>
          ))}
        </div>

        <div
          className={classNames(
            'my-auto flex-1 xl:mr-[100px] xl:text-left 2xl:mr-[154px]',
            isThreeImages
              ? 'text-center max-xl:container'
              : 'text-left md:text-center'
          )}
        >
          <h1
            className="h2 mb-5 xl:mb-7.5"
            data-epi-property-name={meta?.editHints?.title}
            data-testid="Alternative-Hero-Title"
          >
            {title}
          </h1>
          {description && (
            <p
              className="body-one text-grey-200"
              data-epi-property-name={meta?.editHints?.description}
              data-testid="Alternative-Hero-Description"
            >
              {description}
            </p>
          )}
          {link?.name && link.url && (
            <Button
              className="mt-7.5 xl:mt-10"
              link={link}
              editHint={meta?.editHints?.link as string | undefined}
              dataTestId="Alternative-Hero-Link"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default AlternativeHero;
